import React, { KeyboardEvent, ChangeEvent, MutableRefObject } from 'react';
import classnames from 'classnames';

import { useTranslation } from 'hooks';
import { InputSize } from 'helpers/constants';
import { Button } from 'components/shared/';

import styles from './Input.module.scss';
import classNames from 'classnames';

type InputTheme =
  | 'Default'
  | 'Search'
  | 'Plain'
  | 'Flat'
  | 'Light'
  | 'Web'
  | 'WebLight'
  | 'Naked'
  | 'Word';

interface InputProps {
  /** @string - Classname to be passed to the component wrapper element */
  className?: string;
  /** @boolean - If the input should render a copy button and functionality */
  copyOnly?: boolean;
  /** @boolean - Input disabled status */
  disabled?: boolean;
  /** @boolean - If the input should be focused automatically */
  autoFocus?: boolean;
  /** @boolean - If the input has an error */
  error?: boolean;
  /** @boolean - If the input should take up 100% width of it's parent */
  fullWidth?: boolean;
  /** @JSXElement - Icon component instance */
  icon?: JSX.Element;
  /** @string - Classname to be added to the input element */
  inputClassName?: string;
  /** @string - Label text */
  label?: string;
  /** @number - Input maxLength property */
  maxLength?: number;
  /** @string - Input placeholder text */
  placeholder?: string;
  /** @InputSize - Input size variation */
  size?: InputSize;
  /** @any - Suffix to be appended to the label text */
  suffix?: any;
  /** @InputTheme - Input theme variation */
  theme?: InputTheme;
  /** @string | @number - Input value */
  value?: string | number;
  /** @string - Can be text, email, password or search */
  type?: 'text' | 'email' | 'password' | 'search';
  /** @function - On copy callback function */
  onCopy?: (e: any) => void;
  /** @function - On cancel callback function */
  onCancel?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** @function - On click callback function */
  onClick?: (event: any) => void;
  /** @function - On focus callback function */
  onFocus?: (event: any) => void;
  /** @function - On blur callback function */
  onBlur?: (event: any) => void;
  /** @function - On change callback function */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** @function - On key down callback function */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** @function - On submit callback function */
  onSubmit?: (value: string, event: KeyboardEvent<HTMLInputElement>) => void;
}

const TextInput: React.FC<InputProps> = ({
  children,
  size = 'Default',
  theme = 'Default',
  type = 'text',
  autoFocus,
  onBlur,
  onFocus,
  ...props
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const inputRef: MutableRefObject<HTMLInputElement | null> = React.useRef(null);
  let clickHandler = props.onClick;
  let labelElement;

  let keyDownHandler;

  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, inputRef]);

  if (props.onSubmit || props.onCancel || props.onKeyDown) {
    keyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'Enter':
          if (props.onSubmit) {
            const value = (event.target as HTMLInputElement).value;

            event.preventDefault();
            props.onSubmit(value, event);
          }
          break;

        case 'Escape':
          if (props.onCancel) {
            event.preventDefault();
            props.onCancel(event);
          }
          break;

        default:
          if (props.onKeyDown) {
            props.onKeyDown(event);
          }
          break;
      }
    };
  }

  let input = (
    //@ts-ignore
    <input
      ref={inputRef}
      className={classnames(props.inputClassName, {
        [styles.input]: true,
      })}
      type={type}
      value={props.value}
      autoFocus={autoFocus}
      maxLength={props.maxLength}
      disabled={props.copyOnly || props.disabled}
      placeholder={props.placeholder}
      onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange && props.onChange(e)}
      onKeyDown={keyDownHandler}
      onBlur={onBlur}
      onFocus={onFocus}
    />
  );

  if (props.label) {
    labelElement = <div className={styles.label}>{props.label}</div>;
  } else if (props.icon) {
    labelElement = <div className={styles.icon}>{props.icon}</div>;
  }

  let suffixLabel;

  if (props.suffix) {
    suffixLabel = <div className={styles.suffix}>{props.suffix}</div>;
  }

  let copyElement;

  if (props.copyOnly) {
    clickHandler = (event: any) => {
      event.preventDefault();

      if (!!props.value) {
        setCopied(true);
        navigator.clipboard.writeText(String(props.value));
        props.onCopy && props.onCopy(event);
        window.setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    };

    input = <div className={styles.copyInput}>{input}</div>;

    copyElement = (
      <div className={styles.copyButtonContainer}>
        <Button
          className={classNames(styles.copyButton, {
            [styles.copyButtonCopied]: copied,
          })}
          type="Chiclet"
          theme={theme === 'Light' ? 'Light' : undefined}
        >
          {copied ? t('input.copied') : t('input.copy')}
        </Button>
      </div>
    );
  }

  const sizeClass = `size${size}`;
  const styleClass = `theme${theme}`;
  const containerClasses = classnames('controlContainer', props.className, {
    [styles.copyOnly]: props.copyOnly,
    [styles.container]: true,
    [styles.disabled]: props.disabled,
    [styles.error]: props.error,
    [styles.fullWidth]: props.fullWidth,
    [styles[sizeClass] || styles.sizeDefault]: true,
    [styles[styleClass] || styles.styleDefault]: true,
  });

  return (
    <>
      <label className={containerClasses} onClick={clickHandler}>
        {labelElement}
        {input}
        {children}
        {copyElement}
        {suffixLabel}
      </label>
    </>
  );
};

export default TextInput;
